import {
  Alert,
  Box,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  average,
  percentage,
  refereeDisplayName,
  safeCount,
} from "../../helpers/arbitroEstadisticasHelper";

const INTERVALS = [
  ["0_15", "0–15"],
  ["16_30", "16–30"],
  ["31_45", "31–45"],
  ["46_60", "46–60"],
  ["61_75", "61–75"],
  ["76_90_mas", "76+"],
];

const Metric = ({ label, value, detail, color }) => (
  <dl className="rr-referee-metric" style={{ '--metric-accent': color }}>
    <dt>{label}</dt><dd>{value}{detail && <small>{detail}</small>}</dd>
  </dl>
);

const Distribution = ({ label, value, total, color }) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" spacing={2} mb={0.75}>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body2" fontWeight={800} sx={{ fontVariantNumeric: "tabular-nums" }}>
        {value} · {percentage(value, total).toFixed(1)}%
      </Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={Math.min(100, percentage(value, total))}
      aria-label={`${label}: ${value}`}
      sx={{
        height: 8,
        borderRadius: 999,
        bgcolor: (theme) => alpha(theme.palette.text.primary, 0.08),
        "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 999 },
      }}
    />
  </Box>
);

const Section = ({ title, children }) => (
  <Box component="section" className="rr-referee-section">
    <Typography component="h3" variant="h6" fontWeight={900} mb={2}>
      {title}
    </Typography>
    {children}
  </Box>
);

const RefereeReportProfile = ({ summary }) => {
  const { t, i18n } = useTranslation("referees");
  const theme = useTheme();
  const formatter = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 });
  const matches = safeCount(summary?.partidos?.total);
  const totalCards =
    safeCount(summary?.tarjetas?.total?.amarillas) +
    safeCount(summary?.tarjetas?.total?.segundasAmarillas) +
    safeCount(summary?.tarjetas?.total?.rojas);
  const dismissals =
    safeCount(summary?.tarjetas?.total?.segundasAmarillas) +
    safeCount(summary?.tarjetas?.total?.rojas);
  const homeCards =
    safeCount(summary?.tarjetas?.local?.amarillas) +
    safeCount(summary?.tarjetas?.local?.segundasAmarillas) +
    safeCount(summary?.tarjetas?.local?.rojas);
  const awayCards =
    safeCount(summary?.tarjetas?.visitante?.amarillas) +
    safeCount(summary?.tarjetas?.visitante?.segundasAmarillas) +
    safeCount(summary?.tarjetas?.visitante?.rojas);
  const outcomeTotal =
    safeCount(summary?.partidos?.victoriasLocal) +
    safeCount(summary?.partidos?.empates) +
    safeCount(summary?.partidos?.victoriasVisitante);

  return (
    <Stack className="rr-referee-profile" spacing={3}>
      <Box className="rr-referee-identity">
        <Typography component="h2" variant="h5" fontWeight={900}>
          {summary.nombre}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("sample", {
            matches,
            seasons: summary.temporadas?.length || 0,
          })}
        </Typography>
        {summary.competicionesDetalle?.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
            {summary.competicionesDetalle.map((competition) => (
              <Chip
                key={competition.idCompeticion}
                label={competition.nombre || String(competition.idCompeticion)}
                size="small"
              />
            ))}
          </Stack>
        )}
      </Box>

      <div className="rr-referee-metrics">
        {[
          [t("metrics.matches"), matches, t("metrics.sample"), theme.palette.primary.main],
          [
            t("metrics.cardsPerMatch"),
            formatter.format(average(totalCards, matches)),
            t("metrics.cardsTotal", { count: totalCards }),
            theme.palette.warning.main,
          ],
          [
            t("metrics.pointsPerMatch"),
            formatter.format(average(summary.tarjetas?.total?.puntosDisciplinarios, matches)),
            t("metrics.pointsTotal", {
              count: safeCount(summary.tarjetas?.total?.puntosDisciplinarios),
            }),
            theme.palette.error.main,
          ],
          [
            t("metrics.goalsPerMatch"),
            formatter.format(average(summary.goles?.total, matches)),
            t("metrics.goalsTotal", { count: safeCount(summary.goles?.total) }),
            theme.palette.info.main,
          ],
          [
            t("metrics.dismissalsPerMatch"),
            formatter.format(average(dismissals, matches)),
            t("metrics.dismissalsTotal", { count: dismissals }),
            theme.palette.error.dark,
          ],
          [
            t("metrics.homeShare"),
            `${formatter.format(percentage(homeCards, totalCards))}%`,
            `${homeCards} ${t("cards.home")}`,
            theme.palette.success.main,
          ],
          [
            t("metrics.awayShare"),
            `${formatter.format(percentage(awayCards, totalCards))}%`,
            `${awayCards} ${t("cards.away")}`,
            theme.palette.info.dark,
          ],
          [
            t("metrics.penaltiesPerMatch"),
            formatter.format(average(summary.penaltisMarcados?.total, matches)),
            t("metrics.penaltiesTotal", {
              count: safeCount(summary.penaltisMarcados?.total),
            }),
            theme.palette.secondary.main,
          ],
        ].map(([label, value, detail, color]) => (
          <div key={label}>
            <Metric label={label} value={value} detail={detail} color={color} />
          </div>
        ))}
      </div>

      <div className="rr-referee-grid">
        {totalCards > 0 && (
          <div>
            <Section title={t("cards.title")}>
              <TableContainer>
                <Table size="small" aria-label={t("cards.title")}>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell align="right">{t("cards.home")}</TableCell>
                      <TableCell align="right">{t("cards.away")}</TableCell>
                      <TableCell align="right">{t("cards.total", { count: totalCards })}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      ["amarillas", t("cards.yellow")],
                      ["segundasAmarillas", t("cards.secondYellow")],
                      ["rojas", t("cards.red")],
                      ["puntosDisciplinarios", t("cards.points")],
                    ].map(([field, label]) => (
                      <TableRow key={field}>
                        <TableCell>{label}</TableCell>
                        <TableCell align="right">{safeCount(summary.tarjetas?.local?.[field])}</TableCell>
                        <TableCell align="right">{safeCount(summary.tarjetas?.visitante?.[field])}</TableCell>
                        <TableCell align="right">{safeCount(summary.tarjetas?.total?.[field])}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Section>
          </div>
        )}
        <div>
          <Section title={t("results.title")}>
            <Stack spacing={2.25}>
              <Distribution
                label={t("results.home")}
                value={safeCount(summary.partidos?.victoriasLocal)}
                total={outcomeTotal}
                color={theme.palette.success.main}
              />
              <Distribution
                label={t("results.draw")}
                value={safeCount(summary.partidos?.empates)}
                total={outcomeTotal}
                color={theme.palette.warning.main}
              />
              <Distribution
                label={t("results.away")}
                value={safeCount(summary.partidos?.victoriasVisitante)}
                total={outcomeTotal}
                color={theme.palette.info.main}
              />
            </Stack>
          </Section>
        </div>
      </div>

      {totalCards > 0 ? (
        <div className="rr-referee-grid">
          <div>
            <Section title={t("timing.title")}>
              <div className="rr-referee-timing">
                {INTERVALS.map(([key, label]) => (
                  <div key={key}>
                    <Distribution
                      label={`${label}'`}
                      value={safeCount(summary.tarjetas?.total?.porIntervalo?.[key])}
                      total={totalCards}
                      color={theme.palette.primary.main}
                    />
                  </div>
                ))}
              </div>
            </Section>
          </div>
          <div>
            <Section title={t("scoreState.title")}>
              <div className="rr-referee-states">
                {["ganando", "empatando", "perdiendo"].map((state) => (
                  <div key={state}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h5" fontWeight={900} sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {safeCount(summary.tarjetas?.total?.porEstado?.[state])}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t(`scoreState.${state}`)}
                      </Typography>
                    </Box>
                  </div>
                ))}
              </div>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">{t("penalties")}</Typography>
                <Typography fontWeight={900}>{safeCount(summary.penaltisMarcados?.total)}</Typography>
              </Stack>
            </Section>
          </div>
        </div>
      ) : (
        <Alert severity="info">{t("cards.noRecords")}</Alert>
      )}

      <Section title={t("matches.title")}>
        <TableContainer>
          <Table size="small" aria-label={t("matches.title")}>
            <TableHead>
              <TableRow>
                <TableCell>{t("matches.date")}</TableCell>
                <TableCell>{t("matches.match")}</TableCell>
                <TableCell>{t("competition.label")}</TableCell>
                <TableCell align="center">{t("matches.score")}</TableCell>
                <TableCell align="right">{t("matches.yellow")}</TableCell>
                <TableCell align="right">{t("matches.secondYellow")}</TableCell>
                <TableCell align="right">{t("matches.red")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(summary.partidosDetalle || []).slice(0, 10).map((match, index) => (
                <TableRow key={`${match.codActa}-${match.idTemporada}-${index}`}>
                  <TableCell>{match.fecha || t("matches.unknownDate")}</TableCell>
                  <TableCell>
                    {refereeDisplayName(match.equipoLocal, match.idEquipoLocal) || t("matches.unknownHome")}
                    {" – "}
                    {refereeDisplayName(match.equipoVisitante, match.idEquipoVisitante) || t("matches.unknownAway")}
                  </TableCell>
                  <TableCell>
                    {refereeDisplayName(match.nombreCompeticion, match.idCompeticion) || t("competition.unknown")}
                  </TableCell>
                  <TableCell align="center">
                    {match.golesLocal != null && match.golesVisitante != null
                      ? `${match.golesLocal}–${match.golesVisitante}`
                      : t("matches.unknownScore")}
                  </TableCell>
                  <TableCell align="right">
                    {safeCount(match.tarjetasLocal?.amarillas) + safeCount(match.tarjetasVisitante?.amarillas)}
                  </TableCell>
                  <TableCell align="right">
                    {safeCount(match.tarjetasLocal?.segundasAmarillas) +
                      safeCount(match.tarjetasVisitante?.segundasAmarillas)}
                  </TableCell>
                  <TableCell align="right">
                    {safeCount(match.tarjetasLocal?.rojas) + safeCount(match.tarjetasVisitante?.rojas)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>
    </Stack>
  );
};

export default RefereeReportProfile;
